import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Shield, Gift, ArrowRight, Car, Heart, Clock, BookOpen } from 'lucide-react';
import { Countdown } from './Countdown';
const scrollToBuy = () => {
  const buySection = document.getElementById('compra');
  if (buySection) {
    buySection.scrollIntoView({
      behavior: 'smooth'
    });
  }
};
const BookMockup = ({
  title,
  subtitle,
  isBonus = false,
  icon: Icon
}: any) => <div onClick={scrollToBuy} className={`relative w-64 h-84 md:w-72 md:h-96 rounded-r-3xl rounded-l-sm shadow-2xl overflow-hidden flex flex-col p-6 md:p-8 cursor-pointer transform transition-all duration-300 hover:-translate-y-2 hover:shadow-3xl
    ${isBonus ? 'bg-white border-2 border-primary' : 'bg-primary border-l-[12px] border-primary-dark'}`} data-id="element-15">
    <div className="absolute top-0 left-0 w-full h-full bg-black/5 mix-blend-multiply pointer-events-none" data-id="element-16"></div>
    {isBonus && <div className="absolute top-0 left-0 w-3 h-full bg-primary-dark" data-id="element-17"></div>}

    <div className="flex-1 flex flex-col items-center text-center mt-4" data-id="element-18">
      {Icon && <Icon size={40} className={`mb-4 ${isBonus ? 'text-primary-accent' : 'text-primary-accent'}`} data-id="element-19" />}
      <h3 className={`font-serif font-bold ${isBonus ? 'text-xl md:text-2xl text-text' : 'text-3xl md:text-4xl text-text'} leading-tight`} data-id="element-20">
        {title}
      </h3>
      {subtitle && <p className="mt-4 text-sm md:text-base font-medium text-text-light" data-id="element-21">
          {subtitle}
        </p>}
    </div>

    <div className="mt-auto flex flex-col items-center" data-id="element-22">
      <div className="w-16 h-1 bg-primary-accent mb-4 rounded-full" data-id="element-23"></div>
      <p className="font-bold text-xs tracking-widest uppercase text-text-light" data-id="element-24">
        {isBonus ? 'Bono Gratuito' : 'Ebook Digital'}
      </p>
    </div>
  </div>;
export function LandingPage() {
  return <div className="min-h-screen bg-secondary overflow-x-hidden" data-id="element-25">
      {/* HERO SECTION */}
      <section className="relative pt-10 pb-10 md:pt-14 md:pb-12 px-4" data-id="element-26">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center gap-4" data-id="element-27">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="w-full" data-id="element-28">
            <div className="inline-block bg-primary px-4 py-1.5 rounded-full text-primary-accent font-bold text-sm tracking-wide mb-4" data-id="element-29">
              PARA MUJERES QUE QUIEREN VOLVER A MANEJAR
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-text leading-tight mb-2" data-id="element-30">
              Volvé a manejar{' '}
              <span className="text-primary-accent" data-id="element-31">sin miedo</span> en 30 días
            </h1>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="w-full flex justify-center" data-id="element-32">
            <img src="https://cdn.magicpatterns.com/uploads/c4ZW9qpBafoQXDp94iPCS3/Triatlon_En_6HS.png" alt="El freno que está en tu cabeza - Ebook y materiales complementarios" onClick={scrollToBuy} className="w-[96%] h-auto cursor-pointer transform transition-transform duration-300 hover:-translate-y-2 drop-shadow-2xl" data-id="element-33" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="w-full flex flex-col items-center" data-id="element-34">
            <p className="text-base md:text-lg text-text-light mb-5 max-w-2xl" data-id="element-35">
              <strong data-id="element-36">El freno que está en tu cabeza</strong> es una guía para
              recuperar confianza, seguridad e independencia.
            </p>
            <button onClick={scrollToBuy} className="w-full md:w-auto bg-primary-accent hover:bg-primary-hover text-white font-bold text-base md:text-lg py-3 px-8 rounded-full shadow-xl transform transition-transform hover:scale-105 flex items-center justify-center gap-3" data-id="element-37">
              Quiero superar mi miedo <ArrowRight size={20} data-id="element-38" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-12 md:py-14 bg-white px-4" data-id="element-39">
        <div className="max-w-4xl mx-auto" data-id="element-40">
          <h2 className="text-2xl md:text-4xl font-bold text-center mb-8" data-id="element-41">
            ¿Te sentís identificada con alguna de estas situaciones?
          </h2>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-42">
            {['Tenés el registro guardado pero siempre encontrás una excusa para no manejar.', 'Sentís taquicardia, sudoración o pánico con solo pensar en sentarte al volante.', 'Un accidente del pasado te dejó un trauma que no podés superar.', "La voz en tu cabeza te repite constantemente que 'no servís para esto' o que 'vas a chocar'.", 'Dependés de tu pareja, familiares o transporte público para moverte.', 'Te aterra la idea de estacionar o manejar en avenidas transitadas.'].map((point, i) => <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} key={i} className="flex items-start gap-3 p-4 bg-secondary rounded-2xl border border-primary-dark" data-id="element-43">
                <div className="bg-primary p-2 rounded-full text-primary-accent shrink-0 mt-1" data-id="element-44">
                  <Check size={18} data-id="element-45" />
                </div>
                <p className="text-text font-medium text-base" data-id="element-46">{point}</p>
              </motion.div>)}
          </div>
          <div className="mt-8 text-center" data-id="element-47">
            <p className="text-lg text-text-light mb-5" data-id="element-48">
              No estás sola. Y lo más importante:{' '}
              <strong className="text-primary-accent" data-id="element-49">tiene solución.</strong>
            </p>
            <button onClick={scrollToBuy} className="bg-primary text-primary-accent border-2 border-primary-accent hover:bg-primary-accent hover:text-white font-bold text-base py-2.5 px-7 rounded-full transition-colors" data-id="element-50">
              Sí, me pasa esto. Quiero cambiarlo.
            </button>
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="py-12 md:py-14 bg-primary/30 px-4" data-id="element-51">
        <div className="max-w-4xl mx-auto" data-id="element-52">
          <div className="text-center mb-8" data-id="element-53">
            <BookOpen size={36} className="mx-auto text-primary-accent mb-3" data-id="element-54" />
            <h2 className="text-3xl md:text-4xl font-bold text-text" data-id="element-55">
              Contenido que te espera en el ebook
            </h2>
          </div>

          <div className="space-y-3" data-id="element-56">
            {[{
            num: 1,
            title: 'Para las que nunca se animaron a empezar.'
          }, {
            num: 2,
            title: 'Ansiedad y pánico al volante.'
          }, {
            num: 3,
            title: 'El accidente que no te dejó ir.'
          }, {
            num: 4,
            title: 'La voz que te juzga.'
          }, {
            num: 5,
            title: 'El volante como terapia.'
          }, {
            num: 6,
            title: 'Yo manejo, yo decido.'
          }].map((chapter, i) => <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} key={i} className="bg-white p-4 md:p-5 rounded-2xl shadow-sm border border-primary flex items-center gap-4 hover:shadow-md transition-shadow" data-id="element-57">
                <div className="text-3xl md:text-4xl font-serif font-bold text-primary-dark opacity-50" data-id="element-58">
                  {chapter.num.toString().padStart(2, '0')}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-text" data-id="element-59">
                  Capítulo {chapter.num}:{' '}
                  <span className="font-sans font-semibold text-text-light" data-id="element-60">
                    {chapter.title}
                  </span>
                </h3>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 md:py-14 bg-white px-4" data-id="element-61">
        <div className="max-w-6xl mx-auto" data-id="element-62">
          <h2 className="text-2xl md:text-4xl font-bold text-center mb-8" data-id="element-63">
            Mujeres que ya soltaron el freno
          </h2>

          <div className="grid md:grid-cols-3 gap-5 mb-5" data-id="element-64">
            {[{
            name: 'Sofía',
            age: 32,
            city: 'Buenos Aires',
            text: 'Pensé que nunca iba a poder manejar por Panamericana. Me transpiraban las manos de solo pensarlo. Este libro me cambió la cabeza por completo.'
          }, {
            name: 'Mariana',
            age: 45,
            city: 'Rosario',
            text: 'Después de mi choque hace 5 años, no volví a tocar un volante. El capítulo 3 fue una revelación. Hoy llevé a mis hijos al colegio sola por primera vez.'
          }, {
            name: 'Lucía',
            age: 28,
            city: 'Córdoba',
            text: 'El capítulo sobre la voz que te juzga me hizo llorar. Era exactamente lo que necesitaba leer para darme cuenta de que yo era mi peor enemiga.'
          }].map((t, i) => <div key={i} className="bg-secondary p-5 rounded-2xl relative" data-id="element-65">
                <div className="flex text-yellow-400 mb-3" data-id="element-66">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-67" />)}
                </div>
                <p className="text-text-light italic mb-4 text-base" data-id="element-68">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3" data-id="element-69">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-accent font-bold text-lg" data-id="element-70">
                    {t.name[0]}
                  </div>
                  <div data-id="element-71">
                    <p className="font-bold text-text text-sm" data-id="element-72">
                      {t.name}, {t.age}
                    </p>
                    <p className="text-xs text-text-light" data-id="element-73">{t.city}</p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-74">
            {[{
            name: 'Valentina',
            age: 51,
            city: 'Mendoza',
            text: 'Siempre dependí de mi marido para moverme. A mis 51 años, por fin saqué el registro y manejo sin miedo. Nunca es tarde para ser libre.'
          }, {
            name: 'Carolina',
            age: 37,
            city: 'La Plata',
            text: 'Los ejercicios prácticos y el diario de conducción me ayudaron a ir paso a paso. ¡Súper recomendado para las que sienten pánico!'
          }].map((t, i) => <div key={i} className="bg-secondary p-5 rounded-2xl relative" data-id="element-75">
                <div className="flex text-yellow-400 mb-3" data-id="element-76">
                  {[...Array(5)].map((_, j) => <Star key={j} size={16} fill="currentColor" data-id="element-77" />)}
                </div>
                <p className="text-text-light italic mb-4 text-base" data-id="element-78">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3" data-id="element-79">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-accent font-bold text-lg" data-id="element-80">
                    {t.name[0]}
                  </div>
                  <div data-id="element-81">
                    <p className="font-bold text-text text-sm" data-id="element-82">
                      {t.name}, {t.age}
                    </p>
                    <p className="text-xs text-text-light" data-id="element-83">{t.city}</p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-12 md:py-14 bg-primary px-4 relative overflow-hidden" data-id="element-84">
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-85"></div>
        <div className="max-w-6xl mx-auto relative z-10" data-id="element-86">
          <div className="text-center mb-8" data-id="element-87">
            <div className="inline-flex items-center gap-2 bg-white px-5 py-2 rounded-full text-primary-accent font-bold text-base mb-3 shadow-md" data-id="element-88">
              <Gift size={20} data-id="element-89" /> OFERTA ESPECIAL
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-text mb-2" data-id="element-90">
              3 bonos GRATIS por el día de hoy
            </h2>
            <p className="text-base md:text-lg text-text-light" data-id="element-91">
              Llevate estas herramientas complementarias sin costo adicional con
              tu compra hoy.
            </p>
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-8" data-id="element-92">
            <BookMockup title="Meditación pre-conducción" subtitle="Audio para la calma" isBonus={true} icon={Heart} data-id="element-93" />
            <BookMockup title="Diario de conducción" subtitle="30 días para registrar progresos" isBonus={true} icon={Clock} data-id="element-94" />
            <BookMockup title="Manual de emergencia" subtitle="Qué hacer si me bloqueo al volante" isBonus={true} icon={Shield} data-id="element-95" />
          </div>
        </div>
      </section>

      {/* BUY SECTION */}
      <section id="compra" className="py-14 md:py-16 bg-white px-4" data-id="element-96">
        <div className="max-w-3xl mx-auto text-center" data-id="element-97">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-3" data-id="element-98">
            Tu libertad al volante empieza hoy
          </h2>
          <p className="text-base md:text-lg text-text-light mb-6" data-id="element-99">
            Acceso inmediato al ebook "El freno que está en tu cabeza" + los 3
            bonos exclusivos.
          </p>

          <div className="bg-secondary border-4 border-primary rounded-3xl p-6 md:p-8 shadow-2xl relative" data-id="element-100">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-primary-accent text-white font-bold py-2 px-6 rounded-full shadow-lg whitespace-nowrap" data-id="element-101">
              OFERTA POR TIEMPO LIMITADO
            </div>

            <Countdown data-id="element-102" />

            <div className="mb-8" data-id="element-103">
              <p className="text-2xl text-text-light line-through mb-2 font-bold opacity-60" data-id="element-104">
                Precio normal: $49.990
              </p>
              <div className="flex justify-center items-start gap-2" data-id="element-105">
                <span className="text-3xl font-bold text-primary-accent mt-2" data-id="element-106">
                  $
                </span>
                <span className="text-7xl md:text-8xl font-black text-primary-accent tracking-tighter" data-id="element-107">
                  19.990
                </span>
              </div>
              <p className="text-sm font-bold text-green-600 mt-2 bg-green-100 inline-block px-3 py-1 rounded-full" data-id="element-108">
                Ahorrás $30.000 ARS
              </p>
            </div>

            <a href="https://checkout.ebookpoint.shop/cart/45328262397999:1" target="_blank" rel="noopener noreferrer" className="block w-full bg-primary-accent hover:bg-primary-hover text-white font-black text-2xl md:text-3xl py-6 px-8 rounded-2xl shadow-[0_10px_0_0_#9d1349] hover:shadow-[0_5px_0_0_#9d1349] hover:translate-y-1 transition-all flex flex-col items-center justify-center gap-2" data-id="element-109">
              <span data-id="element-110">COMPRAR AHORA</span>
              <span className="text-sm font-normal opacity-90" data-id="element-111">
                Acceso inmediato y seguro
              </span>
            </a>

            <div className="mt-8 flex justify-center items-center gap-6 text-text-light" data-id="element-112">
              <div className="flex items-center gap-2" data-id="element-113">
                <Shield size={20} className="text-green-600" data-id="element-114" />
                <span className="text-sm font-medium" data-id="element-115">Pago 100% Seguro</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-116">
                <Clock size={20} className="text-primary-accent" data-id="element-117" />
                <span className="text-sm font-medium" data-id="element-118">Acceso de por vida</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-text text-white py-8 px-4 text-center" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <p className="text-sm text-gray-400 mb-3" data-id="element-121">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
          <p className="text-sm text-gray-400" data-id="element-122">
            © {new Date().getFullYear()} El freno que está en tu cabeza. Todos
            los derechos reservados.
          </p>
        </div>
      </footer>
    </div>;
}